// Kanemori Sanctuary countryside wildlife (M10): the simulation half, pure and deterministic (no Pixi), so it can be
// tested. Client-only and cosmetic: no collision, no server state, nothing sent. Each critter starts at a seeded spot in
// its area (city-art.json countryFx.critters), wanders there, and shies away from players: deer bound off, a tanuki
// scurries away and hides, crows and herons take off and land again further on, koi sink out of sight; butterflies and
// fireflies drift. Only areas near the camera are stepped. country-fx.ts draws them.
import { CITY_ART } from '../../common/src/data';
import { TILE } from '../../common/src/map';
import { hash2 } from '../../common/src/util';

export type CritterKind = 'deer' | 'tanuki' | 'crow' | 'heron' | 'koi' | 'butterfly' | 'firefly';
export interface Critter {
  kind: CritterKind; home: { x: number; y: number; w: number; h: number }; seed: number;
  x: number; y: number; /** height above the ground (px, flying birds) */ z: number; tx: number; ty: number;
  /** 0 idle, 1 walking, 2 fleeing, 3 flying off, 4 hidden, 5 gliding in to land */ state: number; timer: number; face: number; fade: number;
}
interface Area { x: number; y: number; w: number; h: number; kind: CritterKind; count: number }
const CF = CITY_ART.countryFx as { radius: number; critters: Area[] };
/** how near a player may come before it reacts (px), and how fast it walks / flees (px/s) */
export const SHY: Record<CritterKind, { r: number; walk: number; run: number }> = {
  deer: { r: 76, walk: 11, run: 88 }, tanuki: { r: 58, walk: 9, run: 72 }, crow: { r: 52, walk: 14, run: 96 },
  heron: { r: 84, walk: 5, run: 54 }, koi: { r: 24, walk: 9, run: 9 }, butterfly: { r: 0, walk: 16, run: 16 }, firefly: { r: 0, walk: 6, run: 6 },
};

export class CountryWild {
  readonly list: Critter[] = [];
  constructor(areas: Area[] = CF.critters) {
    areas.forEach((a, ai) => {
      const home = { x: a.x * TILE, y: a.y * TILE, w: a.w * TILE, h: a.h * TILE };
      for (let i = 0; i < a.count; i++) {
        const seed = ai * 31 + i * 7 + 1, x = home.x + hash2(seed, 3) * home.w, y = home.y + hash2(5, seed) * home.h;
        this.list.push({ kind: a.kind, home, seed, x, y, z: 0, tx: x, ty: y, state: 0, timer: hash2(seed, 11) * 3, face: hash2(seed, 13) < 0.5 ? -1 : 1, fade: 1 });
      }
    });
  }
  private pick(c: Critter, k: number) {
    c.tx = c.home.x + hash2(c.seed * 17 + k, c.seed) * c.home.w; c.ty = c.home.y + hash2(c.seed, c.seed * 13 + k) * c.home.h;
  }
  /** Step the critters whose area is within `radius` of the camera. `players`: positions of people on foot or in cars. */
  step(dt: number, t: number, cam: { x: number; y: number }, players: { x: number; y: number }[], radius = CF.radius) {
    for (const c of this.list) {
      const h = c.home;
      if (cam.x < h.x - radius || cam.x > h.x + h.w + radius || cam.y < h.y - radius || cam.y > h.y + h.h + radius) continue;
      const S = SHY[c.kind];
      let near: { x: number; y: number } | null = null, nd = Infinity;
      for (const p of players) { const d = Math.hypot(p.x - c.x, p.y - c.y); if (d < nd) { nd = d; near = p; } }
      c.timer -= dt;
      const scared = near && nd < S.r;
      if (c.kind === 'butterfly' || c.kind === 'firefly') { // drift on a slow figure of eight round a wandering point
        if (c.timer <= 0) { this.pick(c, Math.floor(t)); c.timer = 3 + hash2(c.seed, Math.floor(t)) * 4; }
        c.x += (c.tx - c.x) * Math.min(1, dt * 0.4) + Math.cos(t * 2.3 + c.seed) * S.walk * dt;
        c.y += (c.ty - c.y) * Math.min(1, dt * 0.4) + Math.sin(t * 3.1 + c.seed) * S.walk * dt * 0.7;
        c.z = c.kind === 'butterfly' ? 6 + Math.sin(t * 5 + c.seed) * 2 : 8 + Math.sin(t * 0.7 + c.seed) * 4;
        continue;
      }
      if (c.kind === 'koi') { // circle the pond; sink when someone leans over the water
        c.fade += ((scared ? 0.15 : 1) - c.fade) * Math.min(1, dt * 3);
        if (c.timer <= 0 || Math.hypot(c.tx - c.x, c.ty - c.y) < 4) { this.pick(c, Math.floor(t * 3)); c.timer = 2 + hash2(c.seed, Math.floor(t)) * 3; }
        this.toward(c, S.walk, dt);
        continue;
      }
      if (scared && c.state !== 2 && c.state !== 3 && c.state !== 4) { // react: run (or take off) straight away from them
        const a = Math.atan2(c.y - near!.y, c.x - near!.x), far = c.kind === 'heron' ? 150 : 120;
        c.tx = c.x + Math.cos(a) * far; c.ty = c.y + Math.sin(a) * far;
        c.state = c.kind === 'crow' || c.kind === 'heron' ? 3 : 2; c.timer = c.kind === 'tanuki' ? 1.3 : 2.2;
      }
      if (c.state === 2 || c.state === 3) {
        this.toward(c, S.run, dt);
        c.z = c.state === 3 ? Math.min(c.kind === 'heron' ? 20 : 14, c.z + dt * 30) : 0;
        if (c.timer <= 0) {
          if (c.kind === 'tanuki') { c.state = 4; c.timer = 6; } // gone into the undergrowth
          else if (c.state === 3) { this.pick(c, Math.floor(t)); c.state = 5; } // fly back to land somewhere in its area
          else { c.state = 1; this.pick(c, Math.floor(t)); }
        }
      } else if (c.state === 5) { // gliding in to land
        this.toward(c, S.run * 0.6, dt); c.z = Math.max(0, Math.min(c.z, Math.hypot(c.tx - c.x, c.ty - c.y) * 0.3));
        if (Math.hypot(c.tx - c.x, c.ty - c.y) < 3) { c.state = 0; c.z = 0; c.timer = 2; }
      } else if (c.state === 4) { // hidden: comes back to its area once nobody is near
        c.fade = Math.max(0, c.fade - dt * 2);
        if (c.timer <= 0 && (!near || nd > S.r * 2.5)) { this.pick(c, Math.floor(t)); c.x = c.tx; c.y = c.ty; c.state = 0; c.timer = 2; }
      } else {
        c.fade = Math.min(1, c.fade + dt);
        if (c.timer <= 0) { // idle, then a few steps to a new spot (grazing, pecking, wading)
          if (c.state === 0) { this.pick(c, Math.floor(t * 7)); c.state = 1; c.timer = 4 + hash2(c.seed, Math.floor(t)) * 4; }
          else { c.state = 0; c.timer = 1.5 + hash2(Math.floor(t), c.seed) * 3; }
        }
        if (c.state === 1 && Math.hypot(c.tx - c.x, c.ty - c.y) > 2) this.toward(c, S.walk, dt); else if (c.state === 1) c.state = 0;
      }
    }
  }
  private toward(c: Critter, speed: number, dt: number) {
    const dx = c.tx - c.x, dy = c.ty - c.y, d = Math.hypot(dx, dy); if (d < 0.01) return;
    const s = Math.min(d, speed * dt); c.x += dx / d * s; c.y += dy / d * s; if (Math.abs(dx) > 0.5) c.face = dx < 0 ? -1 : 1;
  }
}
